// L'émargement au doigt — sur le téléphone du formateur, ou sur celui de
// l'apprenant.
//
// **Deux liens, un seul écran.** Celui de la formation est au formateur : il
// voit toutes les lignes et son appareil circule. Celui d'un apprenant, envoyé
// une fois que le formateur a validé sa présence, ne porte que ses lignes —
// c'est le serveur qui retranche, et `portee` dit ici pour qui la page est. Une
// apprenante a reçu la feuille entière et y voyait la ligne de sa formatrice,
// qu'elle aurait pu signer.
//
// Pas de compte, pas de mot de passe : un lien, et le jeton fait foi. C'est la
// même mécanique que la signature d'un devis, à une différence près — ici on
// signe plusieurs fois, une par apprenant et par séance, et le formateur reste
// sur la page entre deux signatures.
//
// Le téléphone commande la mise en page : une colonne, des cibles larges, et
// la séance du jour choisie d'avance. Chercher la bonne séance dans une liste
// déroulante, debout devant un groupe, ne marche pas.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, PenLine, CalendarDays, MapPin, Clock, ClipboardCheck, Loader2 } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';
import api from '../api/client';

const jourFr = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
      weekday: 'short', day: '2-digit', month: 'short',
    });
  } catch { return iso; }
};

const dureeFr = (minutes) => {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  if (h && m) return `${h} h ${String(m).padStart(2, '0')}`;
  return h ? `${h} h` : `${m} min`;
};

export default function EmargementSignPublic() {
  const { token } = useParams();
  const [feuille, setFeuille] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [seanceId, setSeanceId] = useState(null);
  const [signature, setSignature] = useState(null); // la ligne en cours de signature
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/emargement/${token}`);
      setFeuille(data);
      setSeanceId((actuel) => {
        if (actuel) return actuel;
        const dujour = data.seances.find((s) => s.date === data.aujourd_hui);
        return (dujour || data.seances[0] || {}).id || null;
      });
    } catch (err) {
      setErreur(err?.response?.data?.detail || "Ce lien n'est pas valide.");
    }
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  const seance = useMemo(
    () => (feuille?.seances || []).find((s) => s.id === seanceId) || null,
    [feuille, seanceId],
  );

  const couleur = feuille?.organisme?.couleur || '#7C5CFC';

  const enregistrer = async (ligne, dataUrl) => {
    setEnvoi(true);
    try {
      await api.post(`/public/emargement/${token}/signer`, {
        ligne_id: ligne.id, signature_b64: dataUrl,
      });
      setSignature(null);
      await charger();
    } catch (err) {
      alert(err?.response?.data?.detail || "La signature n'a pas pu être enregistrée.");
    } finally {
      setEnvoi(false);
    }
  };

  const marquer = async (ligne, statut) => {
    setEnvoi(true);
    try {
      await api.post(`/public/emargement/${token}/signer`, { ligne_id: ligne.id, statut });
      await charger();
    } catch (err) {
      alert(err?.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  if (erreur) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6F7FB]">
        <div className="text-center max-w-sm">
          <div className="text-[15px] font-semibold text-[#1A1B2E] mb-1">Lien indisponible</div>
          <div className="text-[13px] text-[#6B6C85]">{erreur}</div>
        </div>
      </div>
    );
  }

  if (!feuille) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F7FB] text-[13px] text-[#8E8FA5]">
        Chargement…
      </div>
    );
  }

  const apprenants = (seance?.participants || []).filter((p) => p.role === 'apprenant');
  const formateurs = (seance?.participants || []).filter((p) => p.role === 'formateur');
  const signees = (seance?.participants || []).filter((p) => p.signe).length;
  const total = (seance?.participants || []).length;

  return (
    <div className="min-h-screen bg-[#F6F7FB] pb-24">
      {/* En-tête : l'organisme, puis la formation. */}
      <div style={{ background: couleur }} className="px-4 pt-5 pb-4 text-white">
        <div className="flex items-center gap-3 mb-3">
          {feuille.organisme.logo_b64 && (
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src={feuille.organisme.logo_b64} alt="" className="max-w-[34px] max-h-[34px] object-contain" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider opacity-80">
              {feuille.portee ? 'Votre émargement' : "Feuille d'émargement"}
            </div>
            <div className="text-[15px] font-bold leading-tight truncate">{feuille.formation.titre}</div>
          </div>
        </div>
        {feuille.formation.lieu && (
          <div className="flex items-center gap-1.5 text-[12px] opacity-90">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{feuille.formation.lieu}</span>
          </div>
        )}
      </div>

      {/* Les séances : on balaie, la séance du jour est déjà choisie. */}
      <div className="px-3 py-3 overflow-x-auto flex gap-2 bg-white border-b border-[#EFF0F6]">
        {feuille.seances.map((s) => {
          const actif = s.id === seanceId;
          const cejour = s.date === feuille.aujourd_hui;
          return (
            <button
              key={s.id}
              onClick={() => setSeanceId(s.id)}
              data-testid={`seance-${s.id}`}
              className={`shrink-0 px-3 py-2 rounded-xl border text-left transition ${
                actif ? 'text-white border-transparent' : 'bg-white border-[#EFF0F6] text-[#4A4B63]'
              }`}
              style={actif ? { background: couleur } : undefined}
            >
              <div className="text-[12px] font-semibold flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" />
                {jourFr(s.date)}
                {cejour && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${actif ? 'bg-white/25' : 'bg-[#E6FBF7] text-[#0F766E]'}`}>
                    aujourd'hui
                  </span>
                )}
              </div>
              <div className={`text-[11px] mt-0.5 ${actif ? 'opacity-85' : 'text-[#8E8FA5]'}`}>
                {s.debut} — {s.fin}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tout est signe : on le dit, sinon la page se lit comme une page
          vide. Le formateur, lui, a ses compteurs et sa grille. */}
      {feuille.portee && feuille.seances.length > 0
        && (feuille.seances.every((s) => (s.participants || []).every((p) => p.signe))) && (
        <div className="mx-3 mt-4 rounded-2xl bg-[#E6FBF7] border border-[#99F6E4] px-4 py-3">
          <div className="text-[13px] font-semibold text-[#0F766E] flex items-center gap-1.5">
            <Check className="w-4 h-4" /> Votre émargement est signé
          </div>
          <div className="text-[12px] text-[#0F766E]/80 mt-0.5">
            Merci — rien d'autre ne vous est demandé.
          </div>
        </div>
      )}

      {seance && (
        <>
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[12px] text-[#6B6C85]">
              <Clock className="w-3.5 h-3.5" />
              {dureeFr(seance.duree_minutes)}
            </div>
            <div className="text-[12px] font-semibold" style={{ color: couleur }}>
              {signees} / {total} signé{signees > 1 ? 's' : ''}
            </div>
          </div>

          {/* Un apprenant ne note pas les absences — c'est le formateur qui
              dit qui était là, et le serveur le refuse de toute façon : on ne
              montre donc pas un bouton que la route écarterait. */}
          <Bloc titre={feuille.portee ? 'Votre présence' : 'Apprenants'}
                lignes={apprenants} couleur={couleur}
                onSigner={setSignature} onMarquer={marquer} envoi={envoi}
                sansStatut={!!feuille.portee} />
          <Bloc titre="Formateur" lignes={formateurs} couleur={couleur}
                onSigner={setSignature} onMarquer={marquer} envoi={envoi} sansStatut />
        </>
      )}

      {/* La grille, après les signatures : c'est le seul moment où le formateur
          a ses participants sous les yeux et le téléphone en main. Elle est à
          lui, et à lui seul : la servir au bout du lien d'un apprenant lui
          laisserait cocher ses propres critères. Le serveur refuse, et on ne
          la demande même pas. */}
      {!feuille.portee && <Grille token={token} couleur={couleur} />}

      {/* La signature elle-même : plein écran, le doigt a de la place. */}
      {signature && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{signature.nom}</div>
              <div className="text-[11px] text-[#8E8FA5]">Signez dans le cadre ci-dessous</div>
            </div>
            <button onClick={() => setSignature(null)} className="p-2 -mr-2" aria-label="Fermer">
              <X className="w-5 h-5 text-[#8E8FA5]" />
            </button>
          </div>
          <div className="flex-1 p-4">
            <SignaturePad
              initialName={signature.nom}
              onCancel={() => setSignature(null)}
              onSave={(dataUrl) => enregistrer(signature, dataUrl)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Bloc({ titre, lignes, couleur, onSigner, onMarquer, envoi, sansStatut }) {
  if (!lignes.length) return null;
  return (
    <div className="px-3 mb-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] px-1 mb-1.5">
        {titre}
      </div>
      <div className="bg-white rounded-2xl border border-[#EFF0F6] overflow-hidden">
        {lignes.map((l, i) => (
          <div key={l.id}
               className={`px-3 py-3 flex items-center gap-3 ${i ? 'border-t border-[#F3F4F8]' : ''}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] text-[#1A1B2E] leading-tight">{l.nom}</div>
              {l.commanditaire && (
                <div className="text-[11px] text-[#8E8FA5] truncate">{l.commanditaire}</div>
              )}
              {l.statut === 'absent' && (
                <div className="text-[11px] font-semibold text-[#B91C1C] mt-0.5">Absent</div>
              )}
            </div>

            {l.signe ? (
              <div className="flex items-center gap-2 shrink-0">
                {l.signature_b64 && (
                  <img src={l.signature_b64} alt="" className="h-8 w-20 object-contain" />
                )}
                <span className="w-7 h-7 rounded-full bg-[#E6FBF7] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#0F766E]" />
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                {!sansStatut && l.statut !== 'absent' && (
                  <button
                    disabled={envoi}
                    onClick={() => onMarquer(l, 'absent')}
                    className="h-9 px-2.5 rounded-lg border border-[#EFF0F6] text-[12px] text-[#8E8FA5] disabled:opacity-50"
                  >
                    Absent
                  </button>
                )}
                <button
                  disabled={envoi}
                  onClick={() => onSigner(l)}
                  data-testid={`signer-${l.id}`}
                  className="h-9 px-3 rounded-lg text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: couleur }}
                >
                  <PenLine className="w-3.5 h-3.5" />
                  Signer
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// L'évaluation des acquis, critère par critère et participant par participant.
//
// Huit programmes sur quarante-et-un ne se valident pas par un QCM : on ne
// coche pas un massage cardiaque. La grille ne paraît que là où le programme
// la réclame et déclare ses critères — ailleurs, ce bloc n'existe pas.
//
// Trois états, trois boutons larges : on remplit debout, après la formation.
function Grille({ token, couleur }) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null); // participant_id en cours
  const [brouillon, setBrouillon] = useState({});
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/public/emargement/${token}/grilles`);
      setData(d);
    } catch {
      setData({ attend: false, participants: [], criteres: [] });
    }
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  if (!data || !data.attend) return null;

  const ouvrir = (p) => {
    setOuvert(p.participant_id);
    setBrouillon(p.criteres || {});
    setCommentaire(p.commentaire || '');
  };

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await api.post(`/public/emargement/${token}/grilles`, {
        participant_id: ouvert,
        criteres: brouillon,
        commentaire,
      });
      setOuvert(null);
      await charger();
    } catch (err) {
      alert(err?.response?.data?.detail || "L'évaluation n'a pas pu être enregistrée.");
    } finally { setEnvoi(false); }
  };

  const p = (data.participants || []).find((x) => x.participant_id === ouvert);
  const tousPoses = (data.criteres || []).every((c) => brouillon[c.id]);

  return (
    <div className="px-4 pb-6">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2 flex items-center gap-1.5">
        <ClipboardCheck className="w-3.5 h-3.5" /> Évaluation des acquis
      </div>
      <div className="space-y-2">
        {(data.participants || []).map((x) => (
          <button
            key={x.participant_id}
            onClick={() => ouvrir(x)}
            data-testid={`grille-${x.participant_id}`}
            className="w-full text-left rounded-xl border border-[#EFF0F6] bg-white px-3 py-2.5 flex items-center gap-2"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{x.nom}</div>
              <div className="text-[11px] text-[#8E8FA5]">
                {x.detail?.renseignes || 0} / {x.detail?.total || 0} critère(s)
                {x.detail?.non_acquis?.length
                  ? ` · ${x.detail.non_acquis.length} non acquis` : ''}
              </div>
            </div>
            {x.acquis === true && (
              <span className="shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md bg-[#E6FBF7] text-[#0F766E]">
                Acquis
              </span>
            )}
            {x.acquis === false && (
              <span className="shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md bg-[#FEF6F6] text-[#B91C1C]">
                Non acquis
              </span>
            )}
            {x.acquis === null && (
              <span className="shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md bg-[#FEF3E7] text-[#B45309]">
                À remplir
              </span>
            )}
          </button>
        ))}
      </div>

      {ouvert && p && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{p.nom}</div>
              <div className="text-[11px] text-[#8E8FA5]">Évaluation des acquis</div>
            </div>
            <button onClick={() => setOuvert(null)} className="p-2 -mr-2" aria-label="Fermer">
              <X className="w-5 h-5 text-[#8E8FA5]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {(data.criteres || []).map((c) => (
              <div key={c.id}>
                <div className="text-[12.5px] font-semibold text-[#1A1B2E] mb-1.5">{c.libelle}</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {(data.etats || []).map((e) => {
                    const choisi = brouillon[c.id] === e.code;
                    return (
                      <button
                        key={e.code}
                        onClick={() => setBrouillon((b) => ({ ...b, [c.id]: e.code }))}
                        data-testid={`critere-${c.id}-${e.code}`}
                        className={`h-11 rounded-xl text-[11.5px] font-semibold border transition-colors ${
                          choisi
                            ? 'text-white border-transparent'
                            : 'bg-white text-[#6B6C85] border-[#EFF0F6]'
                        }`}
                        style={choisi ? { background: couleur } : undefined}
                      >
                        {e.libelle}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div>
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] mb-1.5">
                Commentaire (facultatif)
              </div>
              <textarea
                rows={3} value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[#EFF0F6] text-[13px]"
                placeholder="Ce qui reste à consolider…"
              />
            </div>
            <div className="text-[11.5px] text-[#8E8FA5]">
              « Acquis » n&rsquo;est accordé que si <strong>tous</strong> les
              critères le sont. Tant qu&rsquo;un critère n&rsquo;est pas
              renseigné, rien n&rsquo;est tranché.
            </div>
          </div>

          <div className="px-4 py-3 border-t border-[#EFF0F6]">
            <button
              onClick={enregistrer}
              disabled={envoi || !tousPoses}
              data-testid="grille-enregistrer"
              title={!tousPoses ? 'Renseignez chaque critère' : undefined}
              className="w-full h-12 rounded-xl text-white text-[14px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: couleur }}
            >
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
